import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Play, ArrowRight, ChevronLeft, ChevronRight, TrendingUp, Film, Tv, Sparkles } from 'lucide-react'
import MovieRow from '../components/MovieRow'
import { getMovies, getGenres } from '../lib/supabase'

export default function Home({ session }) {
  const [heroMovie, setHeroMovie] = useState(null)
  const [sections, setSections] = useState({})
  const [loading, setLoading] = useState(true)
  const [heroIndex, setHeroIndex] = useState(0)

  useEffect(() => {
    async function loadData() {
      try {
        const [popular, trending, latest, movies, series, korean, anime] = await Promise.all([
          getMovies('popular', 1),
          getMovies('trending', 1),
          getMovies('latest', 1),
          getMovies('movies', 1),
          getMovies('series', 1),
          getMovies('korean', 1),
          getMovies('anime', 1),
        ])
        setSections({ popular, trending, latest, movies, series, korean, anime })
        if (popular.length > 0) setHeroMovie(popular[0])
      } catch (e) {
        console.error('Load error:', e)
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [])

  useEffect(() => {
    if (!heroMovie) return
    const interval = setInterval(() => setHeroIndex(i => (i + 1) % Math.min(5, sections.popular?.length || 1)), 5000)
    return () => clearInterval(interval)
  }, [heroMovie, sections.popular])

  const hero = sections.popular?.[heroIndex] || heroMovie

  if (loading) {
    return (
      <div className="pt-20 space-y-8">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="px-4">
            <div className="flex items-center justify-between mb-4">
              <div className="h-6 w-32 bg-dark-800 rounded animate-pulse" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {[...Array(6)].map((_, j) => (
                <div key={j} className="aspect-[2/3] bg-dark-800 rounded-lg animate-pulse" />
              ))}
            </div>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="pt-20">
      {/* Hero */}
      {hero && (
        <section className="relative h-[60vh] min-h-[400px] max-h-[600px] mb-8">
          {hero.backdrop_url && (
            <img
              src={hero.backdrop_url}
              alt={hero.title}
              className="absolute inset-0 w-full h-full object-cover"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-r from-dark-900 via-dark-900/50 to-transparent" />
          <div className="relative max-w-7xl mx-auto h-full px-4 flex items-end pb-16">
            <div className="max-w-3xl animate-fade-in">
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="px-3 py-1 bg-primary-500 text-white text-xs font-semibold rounded">{hero.type === 'series' ? 'Series' : 'Film'}</span>
                {hero.year && <span className="px-3 py-1 bg-dark-800 text-white text-xs font-medium rounded">{hero.year}</span>}
                {hero.genres && hero.genres.slice(0, 3).map((g) => (
                  <span key={g} className="px-3 py-1 bg-dark-800 text-gray-300 text-xs rounded">{g}</span>
                ))}
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black mb-4 leading-tight">{hero.title}</h1>
              <p className="text-gray-300 text-lg mb-6 max-w-2xl line-clamp-3">{hero.description || 'Tidak ada deskripsi.'}</p>
              <div className="flex flex-wrap gap-3">
                <Link to={`/movie/${hero.slug}`} className="flex items-center gap-2 px-6 py-3 bg-primary-500 text-white font-semibold rounded-lg hover:bg-primary-600 transition-colors group">
                  <Play className="w-5 h-5" /> Tonton Sekarang
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link to={`/movie/${hero.slug}`} className="flex items-center gap-2 px-6 py-3 bg-dark-800/80 text-white font-semibold rounded-lg hover:bg-dark-700/80 transition-colors backdrop-blur-sm border border-dark-700">
                  <Sparkles className="w-5 h-5" /> Detail
                </Link>
              </div>
            </div>
          </div>
          {/* Hero indicators */}
          {sections.popular && sections.popular.length > 1 && (
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2">
              {sections.popular.slice(0, 5).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setHeroIndex(i)}
                  className={`w-2 h-2 rounded-full transition-all ${i === heroIndex ? 'bg-primary-500 w-6' : 'bg-white/40 hover:bg-white/60'}`}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {/* Sections */}
      <div className="max-w-7xl mx-auto px-4 space-y-8">
        <MovieRow title="Trending Sekarang" movies={sections.trending} href="/trending" />
        <MovieRow title="Terpopuler" movies={sections.popular} href="/genre/action" />
        <MovieRow title="Rilis Terbaru" movies={sections.latest} href="/genre/action" />
        <MovieRow title="Film" movies={sections.movies} href="/genre/action" />
        <MovieRow title="Series TV" movies={sections.series} href="/genre/action" />
        <MovieRow title="Drama Korea" movies={sections.korean} href="/genre/drama" />
        <MovieRow title="Anime" movies={sections.anime} href="/genre/animation" />
      </div>
    </div>
  )
}
